import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CalendarCheck, Eye, EyeOff, LogOut, ShieldCheck, Sparkles, Trash2, Users, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "@/data/content";
import { useAuth } from "@/context/AuthContext";

const DAY_NAMES = { 1: "Day 1", 2: "Day 2", 3: "Day 3" };

export default function WishAdmin({ open, onClose, onOpenAuth }) {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("wishes");
  const [wishes, setWishes] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  const [checking, setChecking] = useState([]);
  const [confirmId, setConfirmId] = useState(null);

  const loadWishes = () => {
    axios
      .get(`${API_URL}/wishes/all`, { withCredentials: true })
      .then(({ data }) => setWishes(data))
      .catch(() => toast.error("Could not load wishes — are you signed in as admin?"));
  };

  const loadRsvps = () => {
    axios
      .get(`${API_URL}/rsvps`, { withCredentials: true })
      .then(({ data }) => setRsvps(data))
      .catch(() => toast.error("Could not load RSVPs — are you signed in as admin?"));
  };

  useEffect(() => {
    if (open && user?.role === "admin") {
      if (tab === "wishes") loadWishes();
      else loadRsvps();
    }
  }, [open, user, tab]);

  const toggle = async (w) => {
    try {
      await axios.patch(`${API_URL}/wishes/${w.id}`, { hidden: !w.hidden }, { withCredentials: true });
      setWishes((prev) => prev.map((x) => (x.id === w.id ? { ...x, hidden: !w.hidden } : x)));
      window.dispatchEvent(new CustomEvent("doon:wishes-changed"));
      toast.success(w.hidden ? "Wish is live on the wall again" : "Wish hidden from the public wall");
    } catch (e) {
      toast.error("Could not update the wish");
    }
  };

  const remove = async (w) => {
    try {
      await axios.delete(`${API_URL}/wishes/${w.id}`, { withCredentials: true });
      setWishes((prev) => prev.filter((x) => x.id !== w.id));
      window.dispatchEvent(new CustomEvent("doon:wishes-changed"));
      toast.success("Wish deleted");
    } catch (e) {
      toast.error("Could not delete the wish");
    } finally {
      setConfirmId(null);
    }
  };

  const aiCheck = async (w) => {
    setChecking((c) => [...c, w.id]);
    try {
      const { data } = await axios.post(`${API_URL}/admin/ai-check/${w.id}`, {}, { withCredentials: true });
      setWishes((prev) => prev.map((x) => (x.id === w.id ? { ...x, ai_verdict: data.verdict, ai_reason: data.reason } : x)));
      toast[data.verdict === "clean" ? "success" : "warning"](`AI check: ${data.verdict.toUpperCase()} — ${data.reason}`);
    } catch (e) {
      toast.error("AI check failed — is the LLM key configured?");
    } finally {
      setChecking((c) => c.filter((id) => id !== w.id));
    }
  };

  const headcount = rsvps.reduce((s, r) => s + (r.guests || 1), 0);

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[95] flex items-center justify-center p-4" data-testid="admin-modal">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="admin-modal-backdrop" />
          <motion.div
            initial={{ y: 40, opacity: 0, scale: 0.96 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ type: "spring", damping: 26, stiffness: 240 }}
            className="relative z-10 flex max-h-[86vh] w-full max-w-2xl flex-col rounded-3xl border border-line/25 bg-ink"
          >
            <div className="flex items-center justify-between border-b border-line/15 p-6">
              <div className="flex items-center gap-3">
                <ShieldCheck className="h-5 w-5 text-gold" />
                <div>
                  <h3 className="font-display text-lg font-bold text-fg" data-testid="admin-modal-title">The Curator's Desk</h3>
                  <p className="text-xs text-soft">
                    {tab === "wishes" ? `${wishes.length} wishes · ${wishes.filter((w) => w.hidden).length} hidden` : `${rsvps.length} RSVPs · ${headcount} attending`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={logout} data-testid="admin-logout-button" aria-label="Sign out" className="glass flex h-10 w-10 items-center justify-center rounded-full text-soft transition-colors hover:text-gold">
                  <LogOut className="h-4 w-4" />
                </button>
                <button onClick={onClose} aria-label="Close" data-testid="admin-modal-close" className="glass flex h-10 w-10 items-center justify-center rounded-full text-soft transition-colors hover:text-gold">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {user?.role !== "admin" ? (
              <div className="flex flex-col items-center gap-4 p-10 text-center">
                <ShieldCheck className="h-10 w-10 text-gold" />
                <p className="text-soft" data-testid="admin-gate-message">
                  {user ? "This account doesn't have curator access." : "Sign in with the curator account to moderate the Wish Wall and RSVPs."}
                </p>
                {!user && (
                  <button onClick={() => { onClose(); onOpenAuth(); }} data-testid="admin-gate-login-button" className="rounded-full bg-gold px-8 py-3 text-xs font-bold tracking-widest text-navy transition-transform hover:scale-105">
                    SIGN IN
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="flex gap-2 border-b border-line/15 px-6 py-4" data-testid="admin-tabs">
                  {[["wishes", "Wish Wall"], ["rsvps", "RSVPs"]].map(([id, label]) => (
                    <button
                      key={id}
                      onClick={() => setTab(id)}
                      data-testid={`admin-tab-${id}`}
                      className={`flex items-center gap-2 rounded-full px-5 py-2 text-xs font-bold tracking-widest transition-all duration-300 ${tab === id ? "bg-gold text-navy" : "glass text-soft hover:text-fg"}`}
                    >
                      {id === "wishes" ? <Sparkles className="h-3.5 w-3.5" /> : <Users className="h-3.5 w-3.5" />} {label.toUpperCase()}
                    </button>
                  ))}
                </div>

                <div className="overflow-y-auto p-6" data-testid={tab === "wishes" ? "admin-wish-list" : "admin-rsvp-list"}>
                  {tab === "wishes" ? (
                    <div className="space-y-3">
                      {wishes.map((w) => (
                        <motion.div key={w.id} layout className={`glass rounded-2xl p-4 ${w.hidden ? "opacity-55" : ""}`} data-testid={`admin-wish-row-${w.id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="font-serif2 text-[15px] italic text-fg/90">"{w.message}"</p>
                              <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.15em]">
                                <span className="font-semibold normal-case text-fg">{w.name}</span>
                                <span className="rounded-full border border-gold/30 px-2 py-0.5 text-gold">{w.role}</span>
                                <span className={`rounded-full px-2 py-0.5 ${w.hidden ? "bg-red-400/15 text-red-300" : "bg-green-400/15 text-green-300"}`}>
                                  {w.hidden ? "Hidden" : "Live"}
                                </span>
                                {w.ai_verdict && (
                                  <span className={`rounded-full px-2 py-0.5 ${w.ai_verdict === "clean" ? "bg-green-400/15 text-green-300" : "bg-amber-400/15 text-amber-300"}`} data-testid={`admin-ai-verdict-${w.id}`}>
                                    AI: {w.ai_verdict}{w.ai_reason ? ` · ${w.ai_reason}` : ""}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <button
                                onClick={() => aiCheck(w)}
                                disabled={checking.includes(w.id)}
                                aria-label="AI check"
                                data-testid={`admin-ai-check-${w.id}`}
                                className="glass flex h-9 w-9 items-center justify-center rounded-full text-soft transition-colors hover:text-gold disabled:opacity-50"
                              >
                                <Sparkles className={`h-4 w-4 ${checking.includes(w.id) ? "animate-pulse text-gold" : ""}`} />
                              </button>
                              <button
                                onClick={() => toggle(w)}
                                aria-label={w.hidden ? "Show wish" : "Hide wish"}
                                data-testid={`admin-toggle-${w.id}`}
                                className="glass flex h-9 w-9 items-center justify-center rounded-full text-soft transition-colors hover:text-gold"
                              >
                                {w.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                              <button
                                onClick={() => (confirmId === w.id ? remove(w) : setConfirmId(w.id))}
                                aria-label="Delete wish"
                                data-testid={`admin-delete-${w.id}`}
                                className={`flex h-9 items-center justify-center rounded-full px-3 text-[10px] font-bold tracking-widest transition-all ${confirmId === w.id ? "bg-red-500 text-white" : "glass text-soft hover:text-red-300"}`}
                              >
                                {confirmId === w.id ? "SURE?" : <Trash2 className="h-4 w-4" />}
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3" data-testid="admin-rsvp-rows">
                      {rsvps.length === 0 && <p className="py-8 text-center text-soft">No RSVPs yet — share the site with the Doon family!</p>}
                      {rsvps.map((r) => (
                        <div key={r.email} className="glass rounded-2xl p-4" data-testid={`admin-rsvp-row-${r.email}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-semibold text-fg">{r.name}</span>
                                <span className="rounded-full border border-gold/30 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-gold">{r.role}</span>
                                <span className="rounded-full bg-green-400/15 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-green-300">{r.guests} GUEST{r.guests === 1 ? "" : "S"}</span>
                              </div>
                              <p className="mt-1 truncate text-xs text-soft">{r.email}</p>
                              <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-soft">
                                {r.days.map((d) => <span key={d} className="rounded-full border border-line/20 px-2 py-0.5">{DAY_NAMES[d]}</span>)}
                                <span className="text-soft/60">{new Date(r.updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
                              </div>
                              {r.note && <p className="mt-2 font-serif2 text-sm italic text-fg/80">"{r.note}"</p>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
